/**
 * LayoutTemplatesMenu
 *
 * Dropdown menu to save / apply / manage scheduling view templates.
 * Renders in the dark filter bar of SchedulingScreen.
 */
import React, { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import {
  Layout,
  Save,
  Settings,
  Star,
  StarOff,
  Trash2,
  Loader2,
  Pencil,
  Check,
  X,
} from 'lucide-react';
import { cn } from '../../lib/utils';

const LayoutTemplatesMenu = ({
  templates = [],
  onApplyTemplate,
  onSaveCurrent,
  onUpdateTemplate,
  onDeleteTemplate,
}) => {
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showManageDialog, setShowManageDialog] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveDescription, setSaveDescription] = useState('');
  const [saveIsDefault, setSaveIsDefault] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');

  const resetSaveDialog = () => {
    setSaveName('');
    setSaveDescription('');
    setSaveIsDefault(false);
  };

  const handleSave = async (e) => {
    e?.preventDefault();
    if (!saveName.trim()) return;
    setSaving(true);
    try {
      await onSaveCurrent?.(saveName.trim(), saveDescription.trim() || null, saveIsDefault);
      setShowSaveDialog(false);
      resetSaveDialog();
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteCandidate) return;
    setDeletingId(deleteCandidate.id);
    try {
      await onDeleteTemplate?.(deleteCandidate.id);
      setDeleteCandidate(null);
    } finally {
      setDeletingId(null);
    }
  };

  const startRename = (tpl) => {
    setEditingId(tpl.id);
    setEditName(tpl.name);
  };

  const saveRename = async () => {
    if (!editName.trim() || !editingId) return;
    await onUpdateTemplate?.(editingId, { name: editName.trim() });
    setEditingId(null);
    setEditName('');
  };

  const toggleDefault = async (tpl) => {
    await onUpdateTemplate?.(tpl.id, { is_default: !tpl.is_default });
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="h-7 px-2.5 text-xs rounded border font-medium transition-colors whitespace-nowrap bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-600 hover:text-white flex items-center gap-1.5"
            title="Layout templates"
            data-testid="layout-templates-trigger"
          >
            <Layout className="h-3.5 w-3.5" />
            Layout
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-slate-500">
            Saved Templates
          </DropdownMenuLabel>
          {templates.length === 0 ? (
            <div className="px-2 py-3 text-xs text-slate-400 text-center italic">
              No saved templates yet
            </div>
          ) : (
            templates.map((tpl) => (
              <DropdownMenuItem
                key={tpl.id}
                onClick={() => onApplyTemplate?.(tpl)}
                className="flex items-center gap-2 cursor-pointer"
                data-testid={`layout-template-${tpl.id}`}
              >
                {tpl.is_default ? (
                  <Star className="h-3.5 w-3.5 text-amber-500 flex-shrink-0 fill-amber-400" />
                ) : (
                  <div className="w-3.5 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium truncate">{tpl.name}</div>
                  {tpl.description && (
                    <div className="text-[10px] text-slate-400 truncate">{tpl.description}</div>
                  )}
                </div>
              </DropdownMenuItem>
            ))
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => {
              resetSaveDialog();
              setShowSaveDialog(true);
            }}
            className="cursor-pointer"
            data-testid="layout-templates-save-current"
          >
            <Save className="h-3.5 w-3.5 mr-2 text-emerald-600" />
            <span className="text-xs">Save current view…</span>
          </DropdownMenuItem>
          {templates.length > 0 && (
            <DropdownMenuItem
              onClick={() => setShowManageDialog(true)}
              className="cursor-pointer"
              data-testid="layout-templates-manage"
            >
              <Settings className="h-3.5 w-3.5 mr-2 text-slate-500" />
              <span className="text-xs">Manage templates…</span>
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Save dialog */}
      <Dialog open={showSaveDialog} onOpenChange={(o) => { if (!o) { setShowSaveDialog(false); resetSaveDialog(); } }}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Save className="h-4 w-4 text-emerald-600" />
              Save Current View as Template
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Save your current filters, columns, timeline and layout configuration for quick reuse.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="tpl-name" className="text-xs font-medium">
                Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="tpl-name"
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                placeholder="e.g., 3-Week Critical Path"
                maxLength={120}
                autoFocus
                required
                data-testid="layout-template-name-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tpl-desc" className="text-xs font-medium">Description</Label>
              <Textarea
                id="tpl-desc"
                value={saveDescription}
                onChange={(e) => setSaveDescription(e.target.value)}
                placeholder="Optional notes about this view"
                rows={2}
                maxLength={500}
              />
            </div>
            <div className="flex items-start gap-3 p-3 border border-slate-200 rounded-md">
              <Checkbox
                id="tpl-default"
                checked={saveIsDefault}
                onCheckedChange={(v) => setSaveIsDefault(!!v)}
              />
              <div>
                <label htmlFor="tpl-default" className="text-sm font-medium cursor-pointer flex items-center gap-1.5">
                  <Star className="h-3.5 w-3.5 text-amber-500" />
                  Set as default
                </label>
                <p className="text-xs text-slate-500 mt-0.5">
                  Automatically apply this view when you open the scheduler
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" size="sm" onClick={() => { setShowSaveDialog(false); resetSaveDialog(); }} disabled={saving}>
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                disabled={saving || !saveName.trim()}
                data-testid="layout-template-save-button"
              >
                {saving ? (<><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Saving…</>) : (<><Save className="h-4 w-4 mr-1.5" />Save Template</>)}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Manage dialog */}
      <Dialog open={showManageDialog} onOpenChange={setShowManageDialog}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-4 w-4 text-slate-600" />
              Manage Layout Templates
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Rename, set as default, or delete your saved view templates.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[400px] overflow-y-auto space-y-2 py-2">
            {templates.length === 0 ? (
              <div className="text-center py-8 text-xs text-slate-400 italic">
                No templates saved yet
              </div>
            ) : (
              templates.map((tpl) => (
                <div
                  key={tpl.id}
                  className="flex items-center gap-2 p-2.5 border border-slate-200 rounded-md hover:bg-slate-50"
                  data-testid={`manage-template-${tpl.id}`}
                >
                  <button
                    onClick={() => toggleDefault(tpl)}
                    className={cn(
                      "p-1 rounded hover:bg-amber-100 transition-colors",
                      tpl.is_default ? "text-amber-500" : "text-slate-300"
                    )}
                    title={tpl.is_default ? "Remove as default" : "Set as default"}
                  >
                    {tpl.is_default ? (
                      <Star className="h-3.5 w-3.5 fill-amber-400" />
                    ) : (
                      <StarOff className="h-3.5 w-3.5" />
                    )}
                  </button>
                  <div className="flex-1 min-w-0">
                    {editingId === tpl.id ? (
                      <div className="flex items-center gap-1">
                        <Input
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && saveRename()}
                          className="h-7 text-xs"
                          autoFocus
                        />
                        <button onClick={saveRename} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded" title="Save">
                          <Check className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={() => setEditingId(null)} className="p-1 text-slate-400 hover:bg-slate-100 rounded" title="Cancel">
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="text-sm font-medium text-slate-800 truncate">{tpl.name}</div>
                        {tpl.description && (
                          <div className="text-[11px] text-slate-500 truncate">{tpl.description}</div>
                        )}
                      </>
                    )}
                  </div>
                  {editingId !== tpl.id && (
                    <>
                      <button
                        onClick={() => startRename(tpl)}
                        className="p-1 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                        title="Rename"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => setDeleteCandidate(tpl)}
                        className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                        title="Delete"
                        disabled={deletingId === tpl.id}
                      >
                        {deletingId === tpl.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="h-3.5 w-3.5" />
                        )}
                      </button>
                    </>
                  )}
                </div>
              ))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowManageDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteCandidate} onOpenChange={(o) => !o && setDeleteCandidate(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-500" />
              Delete Layout Template
            </AlertDialogTitle>
            <AlertDialogDescription>
              Delete <strong>"{deleteCandidate?.name}"</strong>? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!deletingId}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={handleConfirmDelete}
              disabled={!!deletingId}
            >
              {deletingId ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Deleting…</> : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default LayoutTemplatesMenu;
